// src/components/PermissionManager.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import api from '../lib/api'; 
import './PermissionManager.css';


const AVAILABLE_ROUTES = [
  { path: '/admin/event', description: 'イベント管理 ' },
  { path: '/admin/salary', description: '給与明細管理 ' },
  { path: '/admin/workstime', description: '勤怠管理 ' },
  { path: '/admin/notice', description: 'お知らせ管理 ' },
  { path: "/admin/applications", description:'申請'},
  { path: "/admin/leave", description:'有給休暇管理'},
  { path: "/admin/emergency_contact", description: '緊急連絡先網'},
  { path: "/admin/employee-card-setting", description: '各マスタ管理'},
  { path: "/admin/qr", description: 'QRコード'},
  { path: "/admin/stresscheck", description: 'ストレスチェック'},

];

export default function PermissionManager() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]); 
  const [activePermissions, setActivePermissions] = useState([]); 
  const [matrixData, setMatrixData] = useState([]); 
  
  const [showUserModal, setShowUserModal] = useState(false);
  const [showFuncModal, setShowFuncModal] = useState(false);
  const [selectedEmpId, setSelectedEmpId] = useState('');
  const [selectedRoutePath, setSelectedRoutePath] = useState('');
  
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  // ---- モーダル共通の操作（画面仕様 13.3、bugfix_管理画面テーブルレイアウト_20261005.md BUG-605） ----
  // 背景クリック・Esc で閉じる。選択中の値がある場合は破棄してよいか確認する。
  // 開いている間は背面をスクロールさせず、開いたら選択欄へ、閉じたら開いたボタンへフォーカスを戻す。
  const isModalOpen = showUserModal || showFuncModal;
  const modalSelectRef = useRef(null);
  const modalOpenerRef = useRef(null);

  const closeUserModal = () => {
    setShowUserModal(false);
    setSelectedEmpId('');
  };
  const closeFuncModal = () => {
    setShowFuncModal(false);
    setSelectedRoutePath('');
  };
  // 背景クリック・Esc から閉じるとき（キャンセルボタンは確認なしで閉じる）
  const requestCloseModal = () => {
    const selected = showUserModal ? selectedEmpId : selectedRoutePath;
    if (selected && !window.confirm('選択した内容を破棄して閉じますか？')) return;
    if (showUserModal) closeUserModal();
    if (showFuncModal) closeFuncModal();
  };

  useEffect(() => {
    if (!isModalOpen) return undefined;
    modalOpenerRef.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (modalSelectRef.current) modalSelectRef.current.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      const opener = modalOpenerRef.current;
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }, [isModalOpen]);

  useEffect(() => {
    if (!isModalOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') requestCloseModal();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // requestCloseModal は選択値と開いているモーダルにだけ依存する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, showUserModal, showFuncModal, selectedEmpId, selectedRoutePath]);

  const onOverlayClick = (e) => {
    // 枠（pm-modal-box）の中のクリックでは閉じない
    if (e.target === e.currentTarget) requestCloseModal();
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [empRes, permRes] = await Promise.all([
          api.get('/employees'), 
          api.get('/permissions/matrix/'), 
        ]);
        
        setEmployees(empRes.data || []);

        const rawColumns = permRes.data?.columns || [];
        const sanitizedColumns = rawColumns.map(col => {
          const safeCode = col.code || col.id || `CAN_MANAGE_${col.linkRoute?.split('/').pop().toUpperCase()}`;
          return { ...col, code: safeCode };
        });
        setActivePermissions(sanitizedColumns);

        const fullList = permRes.data?.rows || [];
        const sanitizedList = fullList.map(row => {
          let userPerms = [];
          
          if (Array.isArray(row.permissions)) {
            userPerms = row.permissions.filter(p => p).map(p => String(p).trim());
          } else if (typeof row.permissions === 'string' && row.permissions.trim() !== '') {
            
            userPerms = row.permissions.split(',').map(p => p.trim()).filter(p => p);
          }
          
          return {
            memberId: String(row.memberId),
            name: row.name,
            departmentName: row.departmentName || '未設定',
            permissions: userPerms 
          };
        });

        const filteredList = sanitizedList.filter(row => row.permissions.length > 0);
        setMatrixData(filteredList);

      } catch (err) {
        console.error("権限のデータベースを取得できませんでした:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // 2. チェックボックスを処理 
  const handleCheckboxChange = (empId, permCode) => {
    setMatrixData(prevMatrixData => {
      return prevMatrixData.map(row => {
        if (row.memberId !== empId) return row;

        const currentPerms = Array.isArray(row.permissions) ? row.permissions : [];
        const hasPerm = currentPerms.includes(permCode);

        let updatedPerms;
        if (hasPerm) {
          updatedPerms = currentPerms.filter(code => code !== permCode);
        } else {
          updatedPerms = [...currentPerms, permCode];
        }

        return {
          ...row,
          permissions: updatedPerms
        };
      });
    });
  };

  // 3. 社員の権限を追加
  const handleAddEmployee = () => {
    if (!selectedEmpId) return;
    
    if (matrixData.some(r => r.memberId === selectedEmpId)) {
      alert('この社員はすでに一覧に存在しています！');
      return;
    }

    const empInfo = employees.find(e => e.memberId === selectedEmpId);
    if (empInfo) {
      const newRow = {
        memberId: String(empInfo.memberId),
        name: empInfo.name,
        departmentName: empInfo.departmentName || '未設定',
        permissions: [] 
      };
      setMatrixData([...matrixData, newRow]);
    }
    closeUserModal();
  };

  // 4. 機能を追加 
  const handleAddPermissionColumn = () => {
    if (!selectedRoutePath) return;
    
    const targetRoute = AVAILABLE_ROUTES.find(r => r.path === selectedRoutePath);
    if (!targetRoute) return;

    const autoGeneratedCode = `CAN_MANAGE_${selectedRoutePath.split('/').pop().toUpperCase()}`;

    if (activePermissions.some(p => p.code === autoGeneratedCode)) {
      alert('この機能はすでにテーブルに追加されています！');
      return;
    }

    setActivePermissions([...activePermissions, { 
      code: autoGeneratedCode, 
      name: targetRoute.description,
      linkRoute: targetRoute.path 
    }]);
    
    closeFuncModal();
  };

  // 5.1 テーブルで社員を削除 
  const handleRemoveRow = (empId) => {
    if (window.confirm('この社員の権限をすべて削除し、一覧から外しますか？')) {
      setMatrixData(prev => prev.filter(row => row.memberId !== empId));
    }
  };

  // 5.2. テーブルで機能を削除 
  const handleRemoveColumn = (permCode) => {
    if (window.confirm('この管理機能をテーブルから削除しますか？（社員の権限データは保存時に更新されます）')) {
     
      setActivePermissions(prev => prev.filter(p => p.code !== permCode));
      
      setMatrixData(prevMatrixData => 
        prevMatrixData.map(row => ({
          ...row,
          permissions: row.permissions.filter(code => code !== permCode)
        }))
      );
    }
  };

  // 6. DBにデータを送信、保存
  const handleSaveMatrix = async () => {
    try {
      setMessage('保存中…');
      
      await api.post('/permissions/matrix/save/', { matrix: matrixData, columns: activePermissions });
      setMessage('権限設定を保存しました。');
     
      setMatrixData(prev => prev.filter(row => row.permissions.length > 0));

      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error(err);
      setMessage('保存に失敗しました。');
    }
  };

  if (loading) return <div className="pm-loading">読み込み中…</div>;

  return (
    <div className="pm-container">
      <header className="pm-header">
        <div>
          <h2 className="pm-title">権限マスタ設定 </h2>
          <p className="pm-subtitle">権限を所持している社員のみ表示されています。チェックを入れて権限を付与・剥奪できます。</p>
        </div>
        <button className="pm-btn-back" onClick={() => navigate(-1)}>戻る</button>
          
        <button className="pm-btn-save" onClick={handleSaveMatrix}>保存</button>
      </header>

      {message && <div className="pm-alert-msg">{message}</div>}

      {/* 権限の列が増えると横に長くなるため、枠の中で縦横スクロールさせ、
          見出しと社員コード・氏名の列を固定する（bugfix_管理画面テーブルレイアウト_20261005.md BUG-601） */}
      <div className="pm-table-wrapper adm-table-scroll">
        <table className="pm-matrix-table">
          <thead>
            <tr>
              <th className="pm-col-code adm-sticky-col">社員コード</th>
              <th className="pm-col-name adm-sticky-col">氏名</th>
              <th>所属部署</th>
              {/*}
              {activePermissions.map(perm => (
                <th key={perm.code} className="pm-th-perm">{perm.name}</th>
              ))}
              */}
              {activePermissions.map(perm => (
                <th key={perm.code} className="pm-th-perm">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{perm.name}</span>
                    <button 
                      type="button" 
                      className="pm-btn-col-del" 
                      style={{ background: 'none', border: 'none', color: '#ff4d4f', cursor: 'pointer', marginLeft: '1px' }}
                      onClick={() => handleRemoveColumn(perm.code)}
                    >
                      (🗑️)
                    </button>
                  </div>
                </th>
              ))}
             
              <th key="action-add-column" className="pm-th-action-add">
                <button className="pm-btn-add-col" onClick={() => setShowFuncModal(true)}>
                  ＋ 列追加
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {matrixData.map(row => (
              <tr key={row.memberId}>
                <td className="pm-td-code pm-col-code adm-sticky-col" title={row.memberId}>{row.memberId}</td>
                <td className="pm-td-name pm-col-name adm-sticky-col">{row.name}</td>
                <td className="pm-td-dept">{row.departmentName}</td>
                
                {activePermissions.map(perm => {
                  const isChecked = Array.isArray(row.permissions) && row.permissions.includes(perm.code);
                  const uniqueKey = `${row.memberId}-${perm.code}`;

                  return (
                    <td key={uniqueKey} className="pm-td-check">
                      <input
                        type="checkbox"
                        value={perm.code}
                        checked={isChecked} 
                        onChange={(e) => {
                          e.stopPropagation();
                          handleCheckboxChange(row.memberId, perm.code);
                        }}
                      />
                    </td>
                  );
                })}
              
                <td className="pm-td-delete">
                  <button type="button" className="pm-btn-row-del" onClick={() => handleRemoveRow(row.memberId)}>削除</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 枠が縦スクロールするようになったため、社員追加は枠の外（表の下）に置く */}
      <div className="pm-table-footer-actions">
        <button className="pm-btn-add-row" onClick={() => setShowUserModal(true)}>社員追加</button>
      </div>

      {/* ================= 社員選択 Modal ================= */}
      {showUserModal && (
        <div className="pm-modal-overlay" onClick={onOverlayClick}>
          <div className="pm-modal-box" role="dialog" aria-modal="true">
            <h3>👥 権限設定に対象社員を追加</h3>
            <p className="pm-modal-desc">一覧にない社員を選択して、新しく権限マトリクスに追加します。</p>
            <select 
              ref={modalSelectRef}
              className="pm-modal-select"
              value={selectedEmpId} 
              onChange={e => setSelectedEmpId(e.target.value)}
            >
              <option value="">— 社員を選択してください —</option>
              {employees.map(e => (
                <option key={e.memberId} value={e.memberId}>
                  [{e.memberId}] {e.name} ({e.departmentName || '部署なし'})
                </option>
              ))}
            </select>
            <div className="pm-modal-buttons">
              <button className="pm-modal-btn-cancel" onClick={closeUserModal}>キャンセル</button>
              <button className="pm-modal-btn-submit" onClick={handleAddEmployee} disabled={!selectedEmpId}>追加する</button>
            </div>
          </div>
        </div>
      )}

      {/* ================= MODAL 機能追加 ================= */}
      {showFuncModal && (
        <div className="pm-modal-overlay" onClick={onOverlayClick}>
          <div className="pm-modal-box" role="dialog" aria-modal="true">
            <h3>⚙️ 新しい管理機能（列）を追加</h3>
            <p className="pm-modal-desc">有効化したい社内システム機能を選択してください。</p>
            
            <div className="pm-modal-form-group">
              <label>機能の選択</label>
              <select
                ref={modalSelectRef}
                className="pm-modal-select"
                value={selectedRoutePath}
                onChange={e => setSelectedRoutePath(e.target.value)}
              >
                <option value="">— 機能を選択してください —</option>
                {AVAILABLE_ROUTES
                  .filter(r => !activePermissions.some(ap => ap.linkRoute === r.path))
                  .map(r => (
                    <option key={r.path} value={r.path}>{r.description}</option>
                  ))
                }
              </select>
            </div>
            <div className="pm-modal-buttons">
              <button className="pm-modal-btn-cancel" onClick={closeFuncModal}>キャンセル</button>
              <button className="pm-modal-btn-submit" onClick={handleAddPermissionColumn} disabled={!selectedRoutePath}>
                列を作成
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}